import { useField } from 'components/common-form/hooks/useField';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import type { ButtonsGroupProps } from 'components/shared/ButtonsGroup/ButtonsGroup';
import { ButtonsGroup } from 'components/shared/ButtonsGroup/ButtonsGroup';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { FC, useCallback } from 'react';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';

type Choice = 'Yes' | 'No';

const boolToChoices = (value: boolean | null): Choice[] => {
  switch (value) {
    case true:
      return ['Yes'];
    case false:
      return ['No'];
  }
  return [];
};

const choicesToBool = (value: Choice | null): boolean | null => {
  switch (value) {
    case 'Yes':
      return true;
    case 'No':
      return false;
  }
  return null;
};

const CHOICES: Choice[] = ['Yes', 'No'];
const identity = (item: any) => item;

type CheckBoxButtonsGroupProps = {
  value: boolean | null;
  onChange: (value: boolean | null) => void;
  isNullable?: boolean;
  className?: string;
  tabIndex?: number;
} & Omit<ButtonsGroupProps<Choice>, 'items' | 'selectedItems' | 'onSelectionChanged' | 'translationKey' | 'isRadio'>;

export function CheckBoxButtonsGroup({
  className,
  value,
  translationPrefix,
  isNullable,
  onChange,
  tabIndex,
  ...props
}: CheckBoxButtonsGroupProps) {
  const currentChoice = boolToChoices(value);
  const onChangeCb = useCallback(
    (nextChoice: Choice | null) => {
      if (nextChoice === null && !isNullable) {
        return;
      }
      onChange(choicesToBool(nextChoice));
    },
    [onChange, isNullable],
  );
  return (
    <ButtonsGroup
      className={className}
      items={CHOICES}
      selectedItems={currentChoice}
      onSelectionChanged={items => {
        onChangeCb(items.length <= 0 ? null : items[0]);
      }}
      blurOnClick={isNullable}
      translationPrefix={translationPrefix}
      translationKey={identity}
      isRadio
      tabIndex={tabIndex}
      {...props}
    />
  );
}
export const CheckboxField: FC<ProductNodeBuilderProps> = ({ node: { id }, workspaceType, tabIndex }) => {
  const {
    metadata: { translationKey, isNullable, isReadOnly },
    data,
    setData,
  } = useField<'ToggleField'>(id)!;
  const patchedFieldClassName = usePatchedFieldHighlight(id);

  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${translationKey ?? id}` as any}
      workspaceType={workspaceType}
      className={patchedFieldClassName}
    >
      <CheckBoxButtonsGroup
        id={id}
        onChange={setData}
        value={data}
        className="w-100"
        isNullable={isNullable}
        translationPrefix="form"
        tabIndex={tabIndex}
        isReadonly={isReadOnly}
      />
    </BlockOrientationFormGroup>
  );
};

CheckboxField.displayName = 'CheckboxField';
